/**
 * ブランド識別値の単一ソース。
 *
 * ここの値は 2 つの経路から参照される:
 *   1. ビルド時 — vite.config.ts の brand-html-meta プラグインが index.html の
 *      <title> / <link rel="icon"> を置換し、gtm:false なら GTM ブロックを除去する
 *   2. 実行時 — src/config/brands/*.ts の BrandConfig が serviceName / favicon /
 *      features.gtm として取り込み、applyBrandTheme() が DOM へ適用する
 *
 * ⚠️ vite.config.ts は Node 側で実行されるため、このモジュールは
 *    アセット import (ロゴ PNG 等) や import.meta.env に依存してはならない。
 *    BrandConfig 本体 (ロゴ import あり) を vite.config から読めないのが、
 *    この分離が必要な理由。
 */

export interface BrandHtmlMeta {
  /** サービス名。index.html の <title> 初期値 兼 画面タイトルのサフィックス */
  serviceName: string;
  /**
   * public/ 配下の favicon パス。<link rel="icon"> と <link rel="apple-touch-icon"> の
   * 両方に使われ、ビルド時には成果物ルートへ favicon.ico / apple-touch-icon.png としても
   * 複製される (vite.config.ts の brand-html-meta)。モバイルブラウザは <link rel="icon">
   * より apple-touch-icon やルート直下の /favicon.ico を優先することがあるため、
   * 全ブランド共通の public/favicon.ico がそのまま出ないようにしている。
   */
  favicon: string;
  /** GTM (共通コンテナ) を読み込むか */
  gtm: boolean;
}

/** キーは BrandConfig の `id` (= VITE_BRAND の値) と一致させること */
export const BRAND_HTML_META = {
  default: { serviceName: '商品上品', favicon: '/favicon.png', gtm: true },
  hypeapp: { serviceName: 'SAITE', favicon: '/saite_favicon.png', gtm: true },
  take8: { serviceName: 'TAKE 8', favicon: '/take8_favicon.png', gtm: true },
  usodeki: { serviceName: '嘘でできた世界', favicon: '/usodeki_favicon.png', gtm: true },
} as const satisfies Record<string, BrandHtmlMeta>;

export const DEFAULT_BRAND_HTML_META: BrandHtmlMeta = BRAND_HTML_META.default;

/** 未知の brandId は default にフォールバック (getBrandConfig() と同じ挙動) */
export function getBrandHtmlMeta(brandId: string | undefined): BrandHtmlMeta {
  return (BRAND_HTML_META as Record<string, BrandHtmlMeta>)[brandId ?? 'default']
    ?? DEFAULT_BRAND_HTML_META;
}
